import React, { useEffect, useCallback } from 'react';
import { HiTrash, HiExclamationTriangle } from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export default function ConfirmDeleteModal({ open, doc, onCancel, onConfirm }) {
  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && e.metaKey) onConfirm();
    },
    [onCancel, onConfirm]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open && doc && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            {/* Header with animated warning icon */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50 px-6 py-5">
              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-600 text-white shadow-lg shadow-red-500/40"
                >
                  <HiTrash className="h-6 w-6" />
                </motion.div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-black">Delete Document?</h3>
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    This action cannot be undone
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700 active:scale-95"
                  aria-label="Close"
                >
                  <HiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body with enhanced visual feedback */}
            <div className="px-6 py-6">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="rounded-xl border border-red-200 bg-red-50 p-4"
              >
                <div className="flex items-start gap-3">
                  <HiExclamationTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-bold text-black">{doc?.fileName}</p>
                    <p className="mt-2 text-sm text-gray-700">
                      This document will be permanently removed from the system. All associated data
                      will be lost.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Footer with enhanced buttons */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  Press{' '}
                  <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs">Esc</kbd> to
                  cancel
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onCancel}
                    className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onConfirm}
                    className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:bg-red-700"
                  >
                    Delete Document
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
